<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import useDisappearable from '../../../composables/useDisappearable'
import FlipDigit from './partials/FlipDigit.vue'
const gsapTimeline = useGsapTimeline()

const props = defineProps({
  item: Object,
})

const position = computed(() => props.item?.properties?.position || {})
const settings = computed(() => props.item?.properties || {})

const digitCount = computed(() => {
  if (settings.value.manualDigitCountEnabled && settings.value.manualDigitCount != null) {
    return settings.value.manualDigitCount
  }
  return settings.value.targetNumber?.toString().length || 1
})

const PHASE_RANDOMIZING = 'randomizing'
const PHASE_REVEALING = 'revealing'
const PHASE_DONE = 'done'

const rootElement = ref(null)

const digits = ref([])
const digitIntervals = ref([])
const phase = ref(PHASE_RANDOMIZING)
const digitRefs = ref([])

onMounted(() => {
  gsapTimeline({ name: 'FlipCounter - In' }).fromTo(rootElement.value, { scale: 0.3, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5, ease: 'cubic-bezier(0.68,-0.55,0.27,1.55)' })

  digits.value = Array.from({ length: digitCount.value }, () => Math.floor(Math.random() * 10))

  digitIntervals.value.forEach((interval) => clearInterval(interval))
  digitIntervals.value = []

  for (let i = 0; i < digitCount.value; i++) {
    const baseSpeed = Number(settings.value.randomDigitChangeSpeed) || 100

    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        digits.value[i] = (digits.value[i] + 1) % 10
      }, baseSpeed + 10)
      digitIntervals.value[i] = interval
    }, (digitCount.value - i) * 200)
    digitIntervals.value[i] = timeout
  }

  const randomPhaseDuration = Number(settings.value.randomPhaseDuration) || 2000
  setTimeout(() => {
    phase.value = PHASE_REVEALING
    digitIntervals.value.forEach((intervalOrTimeout) => clearInterval(intervalOrTimeout))
    digitIntervals.value = []

    const targetStr = settings.value.targetNumber?.toString().replace('.', ',') || ''
    const paddedTarget = targetStr.padStart(digitCount.value, '0').split('')

    const revealDigitDuration = Number(settings.value.revealDigitDuration) || 200
    let revealIdx = digitCount.value - 1

    function revealNext() {
      if (revealIdx < 0) {
        phase.value = PHASE_DONE
        return
      }
      digits.value[revealIdx] = paddedTarget[revealIdx]
      const digitEl = digitRefs.value[revealIdx]
      if (digitEl) {
        gsapTimeline({ name: `FlipCounter - DigitBounce-${revealIdx}` })
          .to(digitEl, { scale: 1.05, duration: 0.12, ease: 'power1.out' })
          .to(digitEl, { scale: 0.98, duration: 0.12, ease: 'power1.in' })
          .to(digitEl, { scale: 1, duration: 0.16, ease: 'power1.out' })
      }
      revealIdx--
      if (revealIdx >= 0) {
        setTimeout(revealNext, revealDigitDuration)
      } else {
        phase.value = PHASE_DONE
      }
    }

    revealNext()
  }, randomPhaseDuration)
})

const { onDisappear } = useDisappearable()
onDisappear(() =>
  gsapTimeline({ name: 'FlipCounter - Out' }).to(rootElement.value, {
    scale: 0.3,
    opacity: 0,
    duration: 0.5,
    ease: 'cubic-bezier(0.68,-0.55,0.27,1.55)',
  })
)

onBeforeUnmount(() => {
  digitIntervals.value.forEach((intervalOrTimeout) => clearInterval(intervalOrTimeout))
  digitIntervals.value = []
})
</script>

<template>
  <div
    ref="rootElement"
    class="absolute flex"
    :style="{
      left: position.xLeft + 'px',
      top: position.yTop + 'px',
      fontSize: position.fontSize + 'px',
    }"
  >
    <div class="w-full h-full flex items-center justify-center gap-2">
      <span v-for="(digit, idx) in digits" :key="idx" :ref="(el) => (digitRefs[idx] = el)" class="inline-block">
        <FlipDigit :digit="digit" :font-size="position.fontSize" :duration="settings.randomDigitChangeSpeed" />
      </span>
    </div>
  </div>
</template>
